import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AttendanceWeekSummaryDto,
  AtRiskStudentRowDto,
  BehaviorCasesResponseDto,
  BillingInsightDto,
  DashboardEventDto,
  DashboardMessagesResponseDto,
  DemographicsResponseDto
} from '../models/dashboard-widgets.model';
import { SchoolContextService } from './school-context.service';

/**
 * Aggregated dashboard widgets (openSIS-style). All paths under /api/v1/dashboard.
 * Each call sends academicYear + termId when backend supports filtering.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardWidgetsService {

  private readonly base = `${environment.apiUrl}/api/v1/dashboard`;

  constructor(
    private http: HttpClient,
    private context: SchoolContextService
  ) { }

  private ctxParams(): HttpParams {
    return new HttpParams()
      .set('academicYear', this.context.getAcademicYear())
      .set('termId', this.context.getTermId());
  }

  /** ISO date (Monday) for the week to display */
  getAttendanceWeek(weekStartMonday: string): Observable<AttendanceWeekSummaryDto | null> {
    const params = this.ctxParams().set('weekStart', weekStartMonday);
    return this.http.get<AttendanceWeekSummaryDto>(`${this.base}/attendance-week`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getOpenBehaviorCases(): Observable<BehaviorCasesResponseDto | null> {
    const params = this.ctxParams();
    return this.http.get<BehaviorCasesResponseDto>(`${this.base}/behavior-cases`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getBillingInsight(fromIso: string, toIso: string): Observable<BillingInsightDto | null> {
    let params = this.ctxParams().set('from', fromIso).set('to', toIso);
    return this.http.get<BillingInsightDto>(`${this.base}/billing-insight`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getDemographics(dimension: 'GENDER' | 'ETHNICITY'): Observable<DemographicsResponseDto | null> {
    const params = this.ctxParams().set('dimension', dimension);
    return this.http.get<DemographicsResponseDto>(`${this.base}/students/demographics`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getStudentsHighAbsence(minPercent?: number): Observable<AtRiskStudentRowDto[] | null> {
    let params = this.ctxParams();
    if (minPercent != null) {
      params = params.set('minAbsencePercent', String(minPercent));
    }
    return this.http.get<AtRiskStudentRowDto[]>(`${this.base}/students/high-absence`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getStudentsLowestGpa(limit?: number): Observable<AtRiskStudentRowDto[] | null> {
    let params = this.ctxParams();
    if (limit != null) {
      params = params.set('limit', String(limit));
    }
    return this.http.get<AtRiskStudentRowDto[]>(`${this.base}/students/lowest-gpa`, { params }).pipe(
      catchError(() => of(null))
    );
  }

  getMessages(): Observable<DashboardMessagesResponseDto | null> {
    return this.http.get<DashboardMessagesResponseDto>(`${this.base}/messages`, { params: this.ctxParams() }).pipe(
      catchError(() => of(null))
    );
  }

  getUpcomingEvents(): Observable<DashboardEventDto[] | null> {
    return this.http.get<DashboardEventDto[]>(`${this.base}/events`, { params: this.ctxParams() }).pipe(
      catchError(() => of(null))
    );
  }
}
